import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { webappLogin, checkUsername } from '../services/webappApi';
import ThemeContext from '../ThemeContext';

// Default theme to use if context is undefined
const defaultTheme = {
  name: 'default',
  colors: {
    primary: '#f97316',
    danger: '#dc3545',
    bgPrimary: '#ffffff',
    textWhite: '#ffffff',
    borderColor: '#dee2e6',
  },
  gradients: {
    primary: '#0d6efd',
  },
};

const WebAppLogin = ({ onLoginClick, showForm = false }) => {
  const { theme = defaultTheme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [usernameValid, setUsernameValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // controls whether password field is shown (step 2)
  const [passwordVisible, setPasswordVisible] = useState(false); // controls eye / eye-slash (text vs password)

  const handleUsernameChange = (e) => {
    setFormData({ ...formData, username: e.target.value });
    setError('');
    setShowPopup(false);
  };

  const handlePasswordChange = (e) => {
    setFormData({ ...formData, password: e.target.value });
    setError('');
    setShowPopup(false);
  };

  const handleNext = async (e) => {
    e.preventDefault();
    if (!formData.username) {
      setError('Record not found or user deactivated.');
      setShowPopup(true);
      return;
    }
    try {
      await checkUsername({ username: formData.username });
      setUsernameValid(true);
      setShowPassword(true);
      setError('');
      setShowPopup(false);
    } catch (err) {
      setError('Record not found or user deactivated.');
      setUsernameValid(false);
      setShowPassword(false);
      setShowPopup(true);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!formData.password) {
      setError('Password is required');
      setShowPopup(true);
      return;
    }
    try {
      const response = await webappLogin(formData);
      localStorage.setItem('webapp_token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      navigate('/session', { replace: true });
    } catch (err) {
      const errorMessage =
        err.response?.data?.error && typeof err.response.data.error === 'object'
          ? Object.values(err.response.data.error)[0][0]
          : 'The username and/or password you entered is incorrect.';
      setError(errorMessage);
      setShowPopup(true);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setError('');
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible((prev) => !prev);
  };

  return (
    <div
      className={showForm ? 'd-flex justify-content-center align-items-center' : ''}
      style={showForm ? { width: '100%', minHeight: '80vh' } : {}}
    >
      <style>
        {`
          .form-control:focus {
            outline: none;
            box-shadow: none;
            border-color: ${theme.colors.borderColor};
          }
        `}
      </style>

      <div
        className="card"
        style={{
          borderRadius: '16px',
          background: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.primary}`,
          minHeight: showForm ? 'auto' : '300px',
          maxHeight: showForm ? '450px' : '300px',
          width: '100%',
          maxWidth: '400px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'center',
          transition: 'transform 0.2s ease',
        }}
      >
        {/* Header like Join page: "Login as Member" */}
        {showForm && (
          <div
            className="card-header"
            style={{
              background: theme.gradients.primary,
              color: theme.colors.textWhite,
              borderRadius: '16px 16px 0 0',
              padding: 16,
              textAlign: 'center',
            }}
          >
            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Login as Member</h3>
          </div>
        )}

        <div className="card-body p-4 d-flex flex-column align-items-center justify-content-center">
          {showForm ? (
            <>
              {showPopup && error && (
                <div
                  className="modal show d-block"
                  tabIndex="-1"
                  style={{ background: 'rgba(0, 0, 0, 0.5)' }}
                >
                  <div className="modal-dialog modal-dialog-centered">
                    <div
                      className="modal-content"
                      style={{
                        borderRadius: '10px',
                        border: `2px solid ${theme.colors.primary}`,
                        background: theme.colors.bgPrimary,
                        fontSize: '12px',
                      }}
                    >
                      <div className="modal-body text-center">
                        <h5 className="text-danger mb-3">Error</h5>
                        <p className="mb-3" style={{ color: '#000000' }}>
                          {error}
                        </p>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={closePopup}
                          style={{
                            fontSize: '12px',
                            padding: '6px 12px',
                            background: theme.colors.danger,
                            border: 'none',
                            borderRadius: '5px',
                          }}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleLoginSubmit}>
                <div className="mb-3">
                  <label htmlFor="username" className="form-label">
                    Username
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleUsernameChange}
                    placeholder="Enter username"
                    required
                    style={{ width: '330px' }}
                    disabled={showPassword}
                  />
                </div>

                {showPassword && (
                  <div className="mb-3">
                    <label htmlFor="password" className="form-label">
                      Password
                    </label>
                    <div style={{ position: 'relative', width: '330px' }}>
                      <input
                        type={passwordVisible ? 'text' : 'password'}
                        className="form-control"
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handlePasswordChange}
                        placeholder="Enter password"
                        required
                        style={{ paddingRight: '2.5rem' }}
                      />
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          padding: 0,
                        }}
                      >
                        <i
                          className={passwordVisible ? 'bi bi-eye' : 'bi bi-eye-slash'}
                          style={{ fontSize: '1.1rem', color: '#6c757d' }}
                        ></i>
                      </button>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  onClick={!showPassword ? handleNext : null}
                  disabled={showPassword && !usernameValid}
                  className="btn w-100 text-white"
                  style={{
                    background: theme.gradients.primary,
                    border: 'none',
                    borderRadius: '8px',
                    padding: '0.75rem 2rem',
                    fontSize: '1rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'opacity 0.2s ease',
                    width: '200px',
                  }}
                  onMouseOver={(e) => (e.target.style.opacity = '0.9')}
                  onMouseOut={(e) => (e.target.style.opacity = '1')}
                >
                  {showPassword ? 'Login' : 'Next'}
                </button>
              </form>
            </>
          ) : (
            <>
              <i
                className="bi bi-person-check-fill"
                style={{
                  fontSize: '4rem',
                  color: theme.colors.primary,
                  marginBottom: '1.5rem',
                  display: 'block',
                  textAlign: 'center',
                }}
              ></i>
              <p className="text-center mb-4">
                If you have an account, click below to go to the login page.
              </p>
              <button
                onClick={onLoginClick}
                className="btn w-100 text-white"
                style={{
                  background: theme.gradients.primary,
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s ease',
                  width: '200px',
                }}
                onMouseOver={(e) => (e.target.style.opacity = '0.9')}
                onMouseOut={(e) => (e.target.style.opacity = '1')}
              >
                Login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebAppLogin;
